import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useParams, useNavigate } from 'react-router-dom';
import * as contract from '@o11y-paas/contract';
import AlertBox from '../components/AlertBox';
import './Pages.css';

function EditOrganization() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { getAccessTokenSilently, isLoading: authLoading } = useAuth0();
    const [organization, setOrganization] = useState<any>([]);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [editData, setEditData] = useState({
        name: '',
        type: '',
        contact_email: '',
        groups: [{ entra_group_name: '', role: contract.userRoles[0] }]
    });
    const [emailError, setEmailError] = useState('');
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState<string | null>(null);
    const [confirmationButtonType, setConfirmationButtonType] = useState(false);
    const [alertType, setAlertType] = useState<'alert' | 'error'>('alert');

    const validateEmail = (email: string) => {
        if(!email || email.trim() === '') {
            return 'Email is required';
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        // Check if the email matches the regex pattern
        if (!emailRegex.test(email)) {
            return 'Enter a valid email address';
        }
        return '';
    };

    useEffect(() => {
        const fetchOrganization = async () => {
            try {
                const token = await getAccessTokenSilently();
                const res = await fetch(`/api/orgs/${id}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                });

                const isJson = res.headers.get('content-type')?.includes('application/json');
                const responseBody = isJson ? await res.json(): null;

                if (!res.ok) {
                    const message =
                        responseBody?.error ||
                        responseBody?.fullError?.name ||
                        res.statusText ||
                        'Unknown error';
                    throw new Error(`${message}`);
                }

                setOrganization(responseBody);
                setEditData({
                    name: responseBody.name,
                    type: responseBody.type,
                    contact_email: responseBody.contact_email,
                    groups: [...responseBody.groups]
                });
            } catch (error) {
                console.error('Error fetching organization:', error);
                setAlertMessage(error instanceof Error ? error.message : 'Unknown error occurred');
                setConfirmationButtonType(true);
                setAlertType('error');
                setShowAlert(true);
            } finally {
                setLoading(false);
            }
        };

        if (!authLoading) {
            fetchOrganization();
        }
    }, [getAccessTokenSilently, authLoading, id]);

    const handleCancel = () => {
        setEditData({
            name: organization.name || '',
            type: organization.type || '',
            contact_email: organization.contact_email || '',
            groups: [...organization.groups]
        });
        setEmailError('');
        navigate(`/manage-organizations`);
    };

    const handleChange = (field, value) => {
        setEditData(prev => ({
            ...prev,
            [field]: value
        }));

        // Validate email when it changes
        if (field === 'contact_email') {
            if (!value || value.trim() === '') {
                setEmailError('Email is required');
            }
            else {
                const emailValidationError = validateEmail(value);
                setEmailError(emailValidationError);
            }
        }
    };

    const handleGroupChange = (index, field, value) => {
        setEditData(prev => {
            const updatedGroups = [...prev.groups];
            updatedGroups[index] = {
                ...updatedGroups[index],
                [field]: value
            };
            return {
                ...prev,
                groups: updatedGroups
            };
        });
    };

    const addGroup = () => {
        setEditData(prev => ({
            ...prev,
            groups: [...prev.groups, { entra_group_name: '', role: contract.userRoles[0] }]
        }));
    };

    const removeGroup = (index) => {
        setEditData(prev => ({
            ...prev,
            groups: prev.groups.filter((_, i) => i !== index)
        }))
    };
    
    const handleConfirmation = () => {
        setShowAlert(false);
        setConfirmationButtonType(false);
        setAlertType('alert');
        navigate(`/manage-organizations`);
    };

    const handleAlertNo = () => {
        setShowAlert(false);
        setAlertType('alert');
    };

    const handleAlertYes = () => {
        setShowAlert(false);
    };

    const handleSave = async () => {

        // Validate email before saving
        const emailValidationError = validateEmail(editData.contact_email);
        if (emailValidationError) {
            setEmailError(emailValidationError);
            return;
        }

        setIsSaving(true);
        try {
            const token = await getAccessTokenSilently();
            const res = await fetch(`/api/orgs/${id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(editData)
            });

            const isJson = res.headers.get('content-type')?.includes('application/json');
            const updatedOrgResponse = isJson ? await res.json(): null;

            if (!res.ok) {
                const message =
                    updatedOrgResponse?.error ||
                    updatedOrgResponse?.fullError?.name ||
                    res.statusText ||
                    'Unknown error';
                throw new Error(`${message}`);
            }

            setOrganization(updatedOrgResponse);

            // Update local state with the response
            setOrganization(prev => ({                
                ...prev,
                ...updatedOrgResponse
            }));
            console.log('Organization updated successfully!');
            navigate(`/manage-organizations`);

        } catch (error) {
            console.error('Error updating organization:', error);
            setAlertMessage(error instanceof Error ? error.message : 'Unknown error occurred');
            setConfirmationButtonType(true);
            setAlertType('error');
            setShowAlert(true);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="page-container">
            <h1>Edit Organization</h1>
            <p>You can edit the details of your organization below.</p>
            
            {loading && <p>Loading...</p>}
            {!loading && !organization && <p>Organization not found.</p>}
            {!loading && organization && (
                <div>
                    <div className="edit-org-form">
                        <div className="edit-org-field">
                            <label>Organization Name</label>
                            <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={editData.name || ''}
                                    onChange={(e) => handleChange('name', e.target.value)}
                                />
                        </div>
                        <div className="edit-org-field">
                            <label htmlFor="code">Organization Code</label>
                            <input
                                type="text"
                                id="code"
                                name="code"
                                value={organization.code}
                                disabled

                            />
                        </div>
                        <div className="edit-org-field">
                            <label>Application Type</label>
                            <select
                                id="type"
                                name="type"
                                value={editData.type || organization.type}
                                onChange={(e) => handleChange('type', e.target.value)}
                            >
                                <option value="" disabled selected hidden>Select an application type</option>
                                {contract.orgTypes.map(t =>
                                    <option value={t}>{t}</option>
                                )}
                            </select>
                        </div>
                        <div className="edit-org-field">
                            <label>Contact Email</label>
                            <div className="email-input-container">
                                <input
                                    type="email"
                                    value={editData.contact_email || ''}
                                    onChange={(e) => handleChange('contact_email', e.target.value)}
                                    className={`edit-input ${emailError ? 'error' : ''}`}
                                />
                                {emailError && <span className="error-message">{emailError}</span>}
                            </div>
                        </div>
                        <div className="edit-org-field">
                            <label htmlFor="code">Region</label>
                            <input
                                type="text"
                                id="region"
                                name="region"
                                value={organization.region}
                                disabled

                            />
                        </div>
                    </div>
                    <div className="groups-section">
                        <h4>Groups and Roles</h4>
                        <p>Map Microsoft Entra Security Groups to Grafana roles. You can add multiple groups.</p>
                        {editData.groups.map((group, index) => (
                            <div key={index} className="group-row">
                                <div className="form-group">
                                    <label htmlFor={`group-${index}`}>Group Name</label>
                                    <input
                                        type="text"
                                        id={`group-${index}`}
                                        value={group.entra_group_name}
                                        onChange={(e) => handleGroupChange(index, 'entra_group_name', e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor={`role-${index}`}>Role</label>
                                    <select
                                        id={`role-${index}`}
                                        value={group.role}
                                        onChange={(e) => handleGroupChange(index, 'role', e.target.value)}
                                    >
                                        {contract.userRoles.map(r =>
                                            <option value={r}>{r}</option>
                                        )}
                                    </select>
                                </div>
                                {
                                    index > 0 &&
                                    <button
                                        className="remove-group-button"
                                        onClick={() => removeGroup(index)}
                                    >
                                        X
                                    </button>
                                }
                            </div>
                        ))}
                        <button
                            className="add-group-button"
                            onClick={addGroup}
                        >
                            Add Another Group
                        </button>
                    </div>
                    <div className="action-buttons">
                        <div className="edit-actions">
                            <button
                                className="save-button"
                                onClick={handleSave}
                                disabled={isSaving || emailError !== ''}
                                title="Save Changes"
                            >
                                {isSaving ? 'Saving...' : 'Save Changes'}
                            </button>
                            <button className="cancel-button" onClick={handleCancel} disabled={isSaving} title="Cancel">
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <AlertBox
                show={showAlert}
                message={alertMessage}
                onClose={confirmationButtonType ? handleConfirmation : handleAlertNo}
                onConfirm={!confirmationButtonType ? handleAlertYes : undefined}
                type={alertType}
            />
        </div>
    );
}

export default EditOrganization;